import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BadLeadDetailsCard from "@/components/badleads/BadLeadDetailsCard";
import BadLeadStatusHistory from "@/components/badleads/BadLeadStatusHistory";
import BadLeadReviewForm from "@/components/badleads/BadLeadReviewForm";

/**
 * Detail page for a single bad-lead report. Owns the review-form state and
 * hands rendering off to the sub-components in `components/badleads/`.
 */
export default function BadLeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [voucher, setVoucher] = useState(null);
  const [form, setForm] = useState({
    status: "",
    review_notes: "",
    credit_amount: "",
    replacement_credit_amount: "",
    replacement_lead_id: "",
    replacement_expires_days: 90,
  });

  const load = async () => {
    const r = await api.get(`/bad-leads/${id}`);
    setData(r.data);
    setForm((f) => ({ ...f, status: r.data.report.status }));
  };

  useEffect(() => {
    load();
    api.get("/bad-leads/_meta/enums").then((r) => setStatuses(r.data.statuses));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!hasPerm("review_bad_leads")) return toast.error("Missing review permission");
    try {
      const payload = {
        status: form.status,
        review_notes: form.review_notes || null,
        credit_amount: form.credit_amount ? Number(form.credit_amount) : null,
        replacement_credit_amount: form.replacement_credit_amount ? Number(form.replacement_credit_amount) : null,
        replacement_lead_id: form.replacement_lead_id || null,
        replacement_expires_days: form.replacement_expires_days ? Number(form.replacement_expires_days) : null,
      };
      const { data: resp } = await api.patch(`/bad-leads/${id}/review`, payload);
      if (resp?.voucher) {
        setVoucher(resp.voucher);
        toast.success(`Replacement voucher issued (expires ${new Date(resp.voucher.expires_at).toLocaleDateString()})`);
      } else {
        toast.success("Review saved");
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (!data) return <div className="label-mono">Loading…</div>;
  const report = data.report;

  return (
    <div>
      <button onClick={() => nav(-1)} className="label-mono mb-3 hover:underline">← Back</button>
      <div className="flex items-end justify-between">
        <div>
          <div className="label-mono">Bad-lead report</div>
          <h1 className="font-chivo font-black text-3xl uppercase tracking-tighter">
            Report {report.id.slice(0, 8)}
          </h1>
          <div className="mt-2"><StatusBadge status={report.status} testId="report-status" /></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <BadLeadDetailsCard report={report} lead={data.lead} />
        <BadLeadStatusHistory history={report.status_history} />
      </div>

      {hasPerm("review_bad_leads") && (
        <BadLeadReviewForm
          form={form}
          setForm={setForm}
          statuses={statuses}
          voucher={voucher}
          onSubmit={submit}
        />
      )}
    </div>
  );
}
